import type { ReactNode, SVGProps } from 'react'

/**
 * Shared SVG wrapper. Every Feather-style icon in the app was
 * cutting-and-pasting the same `viewBox`, `stroke`, and `strokeLinecap`
 * attributes — 20-odd duplicates that drift over time. This component
 * owns the defaults so an icon definition becomes just the path data.
 *
 * Usage:
 *   <Icon><polyline points="21 8 21 21 3 21 3 8" /></Icon>
 *
 * Override anything via the standard SVG prop surface (size, stroke width,
 * className, aria-*). The `size` shorthand sets both width and height.
 */
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  children: ReactNode
  size?: number | string
}

export function Icon({
  children,
  size = 14,
  viewBox = '0 0 24 24',
  fill = 'none',
  stroke = 'currentColor',
  strokeWidth = 1.8,
  strokeLinecap = 'round',
  strokeLinejoin = 'round',
  width,
  height,
  ...rest
}: IconProps) {
  return (
    <svg
      width={width ?? size}
      height={height ?? size}
      viewBox={viewBox}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap={strokeLinecap}
      strokeLinejoin={strokeLinejoin}
      {...rest}
    >
      {children}
    </svg>
  )
}
